import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ModalGeneric } from '../ModalGeneric';
import { useApi } from '../../hooks/api';
import { useGlobal } from '../../hooks/global';
import { inactiveItens, primary, secondary } from '../../theme';
import { ContainerField, Label } from '../../style';
import { TextArea } from '../Inputs/TextArea';
import { featureGuides, FeatureGuide } from '../../utils/featureGuides';
import { revealGuideTarget } from '../../utils/revealGuideTarget';
import './style.css';

interface Progress { code: string; seen_at: string | null; skiped_at: string | null; finished_at: string | null; evaluated: boolean; auto_available: boolean; }

const StarRating: React.FC<{ value: number; onChange: (value: number) => void; size?: number }> = ({ value, onChange, size = 24 }) => {
  const { theme } = useGlobal();
  const [hovered, setHovered] = useState(0);
  const highlighted = hovered || value;
  return <div onMouseLeave={() => setHovered(0)} style={{ display: 'flex', gap: 6, margin: '14px 0' }}>
    {[1, 2, 3, 4, 5].map(star => <button type="button" key={star} aria-label={`${star} estrelas`}
      onMouseEnter={() => setHovered(star)} onFocus={() => setHovered(star)} onBlur={() => setHovered(0)} onClick={() => onChange(star)}
      style={{ padding: 0, border: 0, background: 'transparent', color: star <= highlighted ? secondary[theme] : '#fff', textShadow: star <= highlighted ? undefined : `0 0 1px ${inactiveItens[theme]}`, fontSize: size, lineHeight: 1, cursor: 'pointer' }}>★</button>)}
  </div>;
};

export const FeatureGuideRunner: React.FC = () => {
  const { user } = useApi();
  return user.type === 'root' ? null : <UserFeatureGuideRunner />;
};

const UserFeatureGuideRunner: React.FC = () => {
  const { api } = useApi();
  const { theme, notify, setMenuOpen, isMobile, activeFeatureGuide, startFeatureGuide, updateFeatureGuideStep, clearFeatureGuide } = useGlobal();
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<Progress[]>([]);
  const requestedGuide = new URLSearchParams(location.search).get('guide');
  const requestedStep = Number(new URLSearchParams(location.search).get('step') || 0);
  const [ratingGuide, setRatingGuide] = useState<FeatureGuide | null>(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const completedInSession = useRef(new Set<string>());

  const clearGuideAttention = useCallback(() => {
    document.querySelectorAll('.feature-guide-target').forEach(element => {
      element.classList.remove('feature-guide-target', 'feature-guide-target-with-padding');
    });
    document.body.classList.remove('feature-guide-click-in-progress', 'feature-guide-information-only');
    document.getElementById('nav-sidebar')?.classList.remove('feature-guide-sidebar-guided');
    setTargetRect(null);
  }, []);

  const eligible = useMemo(() => featureGuides.filter(guide => {
    if (isMobile) return false;
    const item = progress.find(value => value.code === guide.code);
    const isGuideRoute = location.pathname === guide.path || requestedGuide === guide.code;
    return isGuideRoute && !!item && !item.skiped_at && !item.finished_at && (item.auto_available || requestedGuide === guide.code);
  }), [isMobile, location.pathname, progress, requestedGuide]);
  const guide = !isMobile && activeFeatureGuide
    ? featureGuides.find(item => item.code === activeFeatureGuide.code)
    : !isMobile && requestedGuide ? eligible.find(item => item.code === requestedGuide) : eligible[0];
  const step = activeFeatureGuide && activeFeatureGuide.code === guide?.code ? activeFeatureGuide.step : requestedStep;

  const load = useCallback(async () => {
    try {
      const data = (await api.get('/feature-guides')).data as Progress[];
      setProgress(data.map(item => completedInSession.current.has(item.code)
        ? { ...item, seen_at: item.seen_at || new Date().toISOString() }
        : item));
    } catch { /* Guides must never interrupt ordinary work. */ }
  }, [api]);
  useEffect(() => {
    if (isMobile) return;
    load();
    window.addEventListener('feature-guides-updated', load);
    return () => window.removeEventListener('feature-guides-updated', load);
  }, [isMobile, location.pathname, load]);
  useEffect(() => {
    if (!isMobile || !activeFeatureGuide) return;
    clearGuideAttention();
    clearFeatureGuide();
  }, [isMobile, activeFeatureGuide, clearFeatureGuide, clearGuideAttention]);
  useEffect(() => {
    if (guide && !activeFeatureGuide) startFeatureGuide(guide.code, Number.isInteger(requestedStep) && requestedStep >= 0 ? requestedStep : 0);
    setRating(0); setComment('');
  }, [guide, activeFeatureGuide, requestedStep, startFeatureGuide]);

  useEffect(() => {
    if (!guide) return;
    const item = progress.find(value => value.code === guide.code);
    if (!item || item.seen_at) return;
    setProgress(current => current.map(value => value.code === guide.code ? { ...value, seen_at: new Date().toISOString() } : value));
    void api.post(`/feature-guides/${guide.code}/seen`).catch(() => undefined);
  }, [guide, progress, api]);

  const current = guide?.steps[step];
  useEffect(() => {
    if (!guide || !current?.targetId || ratingGuide) return;
    clearGuideAttention();
    if (current.targetId.startsWith('link-to-')) setMenuOpen(true);
    let target: HTMLElement | null = null;
    let disposed = false;
    const sidebar = document.getElementById('nav-sidebar');
    document.body.classList.add('feature-guide-click-in-progress');
    if (current.highlightOnly) document.body.classList.add('feature-guide-information-only');
    sidebar?.classList.add('feature-guide-sidebar-guided');
    const onTargetClick = () => {
      if (!current.highlightOnly && !current.targetPath) window.setTimeout(() => updateFeatureGuideStep(step + 1), 0);
    };
    const updateTargetRect = () => {
      if (disposed) return;
      const element = document.getElementById(current.targetId as string);
      if (target !== element) {
        target?.classList.remove('feature-guide-target');
        target?.classList.remove('feature-guide-target-with-padding');
        target?.removeEventListener('click', onTargetClick);
        target = element;
        target?.classList.add('feature-guide-target');
        if (current.targetPadding) target?.classList.add('feature-guide-target-with-padding');
        target?.addEventListener('click', onTargetClick);
        if (target) revealGuideTarget(current.targetId as string);
      }
      if (target) setTargetRect(target.getBoundingClientRect());
    };
    const revealTarget = () => {
      revealGuideTarget(current.targetId as string);
      window.requestAnimationFrame(updateTargetRect);
    };
    revealTarget();
    const firstScrollTimer = window.setTimeout(revealTarget, 150);
    const secondScrollTimer = window.setTimeout(revealTarget, 500);
    const targetTimer = window.setInterval(updateTargetRect, 200);
    const blockOutsideClick = (event: MouseEvent) => {
      const element = event.target as HTMLElement;
      if ((current.highlightOnly || !target?.contains(element)) && !element.closest('.feature-guide-callout')) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('click', blockOutsideClick, true);
    window.addEventListener('resize', updateTargetRect);
    document.addEventListener('scroll', updateTargetRect, true);
    return () => {
      disposed = true;
      window.clearTimeout(firstScrollTimer);
      window.clearTimeout(secondScrollTimer);
      window.clearInterval(targetTimer);
      document.removeEventListener('click', blockOutsideClick, true);
      target?.removeEventListener('click', onTargetClick);
      window.removeEventListener('resize', updateTargetRect);
      document.removeEventListener('scroll', updateTargetRect, true);
      clearGuideAttention();
    };
  }, [guide, step, current, setMenuOpen, updateFeatureGuideStep, ratingGuide, clearGuideAttention]);
  useEffect(() => {
    if (!guide || !current?.targetPath || location.pathname !== current.targetPath) return;
    clearGuideAttention();
    updateFeatureGuideStep(step + 1);
  }, [guide, step, current?.targetPath, location.pathname, updateFeatureGuideStep, clearGuideAttention]);

  const returnToOrigin = () => {
    const origin = activeFeatureGuide?.origin || guide?.path || '/';
    clearFeatureGuide();
    navigate(origin, { replace: true });
  };

  const finish = (showRating = false, returnAfterFinish = true) => {
    if (!guide) return;
    clearGuideAttention();
    const completedGuide = guide;
    completedInSession.current.add(completedGuide.code);
    window.dispatchEvent(new Event('feature-guide-close-filters'));
    const hasEvaluation = !!progress.find(item => item.code === completedGuide.code)?.evaluated;
    setProgress(current => current.map(item => item.code === completedGuide.code ? { ...item, finished_at: new Date().toISOString() } : item));
    if (showRating && !hasEvaluation) setRatingGuide(completedGuide);
    else if (returnAfterFinish) returnToOrigin();
    else clearFeatureGuide();
    void api.post(`/feature-guides/${completedGuide.code}/finished`).catch(() => undefined);
  };

  const next = () => {
    if (!guide) return;
    const nextStep = guide.steps[step + 1];
    if (step + 1 < guide.steps.length) {
      if (nextStep?.path) {
        updateFeatureGuideStep(step + 1);
        navigate(`${nextStep.path}?guide=${guide.code}&step=${step + 1}`);
      }
      else updateFeatureGuideStep(step + 1);
    } else finish(true);
  };

  const previous = () => {
    if (!guide || step === 0) return;
    const previousStep = guide.steps[step - 1];
    if (previousStep?.targetPath && !previousStep.path) {
      updateFeatureGuideStep(step - 1);
      navigate(`${guide.path}?guide=${guide.code}&step=${step - 1}`);
      return;
    }
    if (previousStep?.path) {
      updateFeatureGuideStep(step - 1);
      if (location.pathname !== previousStep.path) navigate(previousStep.path);
      return;
    }
    updateFeatureGuideStep(step - 1);
  };

  const closeGuide = () => {
    if (!guide) return;
    clearGuideAttention();
    const skippedGuide = guide;
    window.dispatchEvent(new Event('feature-guide-close-filters'));
    setProgress(current => current.map(item => item.code === skippedGuide.code ? { ...item, skiped_at: new Date().toISOString() } : item));
    clearFeatureGuide();
    void api.post(`/feature-guides/${skippedGuide.code}/skipped`).catch(() => undefined);
  };

  const advanceGuidedClick = () => {
    if (!current?.targetId) return;
    document.getElementById(current.targetId)?.click();
  };

  const closeRating = () => {
    setRatingGuide(null);
    returnToOrigin();
  };

  const submitRating = async () => {
    if (!ratingGuide) return;
    try { await api.post(`/feature-guides/${ratingGuide.code}/evaluation`, { rating: rating || null, comment }); closeRating(); }
    catch { notify('Não foi possível salvar sua avaliação.', 'error'); }
  };

  if (!guide && !ratingGuide) return null;
  const waitingForClick = !ratingGuide && !!guide && !!current?.targetId && location.pathname !== current.targetPath;
  const calloutTop = targetRect && targetRect.width > window.innerWidth / 2 ? targetRect.bottom + 12 : 105;
  const targetPadding = current?.targetPadding || 0;
  return <>
    {waitingForClick && createPortal(<>{targetRect && <div className="feature-guide-action-overlay" style={{ top: targetRect.top - targetPadding, left: targetRect.left - targetPadding, width: targetRect.width + targetPadding * 2, height: targetRect.height + targetPadding * 2, borderRadius: targetPadding ? 10 : 8, outline: targetPadding ? '3px solid #E20055' : undefined }} />}
      <div className="feature-guide-callout" style={{ position: 'fixed', left: targetRect && targetRect.left > window.innerWidth / 2 ? 140 : undefined, right: targetRect && targetRect.left > window.innerWidth / 2 ? undefined : 24, top: calloutTop, maxHeight: `calc(100vh - ${calloutTop + 12}px)`, overflowY: 'auto', zIndex: 1501, maxWidth: 320, padding: 18, borderRadius: 8, background: primary[theme], color: inactiveItens[theme], boxShadow: '0 4px 18px rgba(0,0,0,.22)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}><div style={{ color: secondary[theme], fontSize: 12, fontWeight: 700 }}>ETAPA {step + 1} DE {guide?.steps.length}</div><button type="button" aria-label="Fechar guia" onClick={closeGuide} style={{ border: 0, background: 'transparent', color: inactiveItens[theme], cursor: 'pointer', fontSize: 24, lineHeight: 1, padding: 0 }}>×</button></div><strong>{current?.title}</strong><p style={{ margin: '8px 0 14px', lineHeight: 1.45 }}>{current?.text}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><button type="button" onClick={previous} style={{ border: 0, background: 'transparent', color: inactiveItens[theme], cursor: 'pointer', padding: 0 }}>Voltar</button><button type="button" onClick={current?.highlightOnly ? next : advanceGuidedClick} style={{ border: 0, borderRadius: 5, padding: '10px 16px', background: secondary[theme], color: '#fff', cursor: 'pointer' }}>{step + 1 === guide?.steps.length ? 'Concluir guia' : 'Avançar'}</button></div>
      </div></>, (current?.openFilters && document.getElementById('guide-filters-container')) || document.body)}
    {guide && !ratingGuide && !waitingForClick && <ModalGeneric open onClose={() => { }}>
      <div style={{ width: 440, maxWidth: 'calc(100vw - 40px)', padding: 28, color: inactiveItens[theme], background: primary[theme] }}>
        <><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}><div style={{ color: secondary[theme], fontSize: 13, fontWeight: 700 }}>{eligible.length > 1 ? `${eligible.length} GUIAS PENDENTES · ` : ''}ETAPA {step + 1} DE {guide.steps.length}</div><button type="button" aria-label="Fechar guia" onClick={closeGuide} style={{ border: 0, background: 'transparent', color: inactiveItens[theme], cursor: 'pointer', fontSize: 28, lineHeight: 1, padding: 0 }}>×</button></div>
          <h2 style={{ margin: '10px 0', fontSize: 23 }}>{current?.title}</h2>
          <p style={{ lineHeight: 1.55, margin: '0 0 24px' }}>{current?.text}</p>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            {step === 0 ? <button type="button" onClick={closeGuide} style={{ border: 0, background: 'transparent', color: inactiveItens[theme], cursor: 'pointer' }}>Pular guia</button> : <button type="button" onClick={previous} style={{ border: 0, background: 'transparent', color: inactiveItens[theme], cursor: 'pointer' }}>Voltar</button>}
            <button type="button" onClick={next} style={{ border: 0, borderRadius: 5, padding: '10px 16px', background: secondary[theme], color: '#fff', cursor: 'pointer' }}>{step + 1 === guide.steps.length ? 'Concluir guia' : 'Próximo'}</button>
          </div></>
      </div>
    </ModalGeneric>}
    {ratingGuide && <ModalGeneric open onClose={() => { }}>
      <div style={{ width: 440, maxWidth: 'calc(100vw - 40px)', padding: 28, color: inactiveItens[theme], background: primary[theme] }}>
        <div style={{ color: secondary[theme], fontSize: 13, fontWeight: 700 }}>GUIA CONCLUÍDO</div><h2 style={{ margin: '10px 0', fontSize: 23 }}>Como foi sua experiência?</h2>
        <p style={{ lineHeight: 1.55 }}>Sua opinião nos ajuda a deixar a Max Locação mais simples para a rotina da imobiliária.</p>
        <StarRating value={rating} onChange={setRating} />
        <ContainerField>
          <Label>Comentário</Label>
          <TextArea value={comment} rows={4} maxLength={200} placeholder="Conte um pouco mais (opcional)" onChange={(value: any) => setComment(value)} />
        </ContainerField>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 14 }}><button type="button" onClick={closeRating} style={{ border: 0, background: 'transparent', color: inactiveItens[theme], cursor: 'pointer' }}>Pular avaliação</button><button type="button" onClick={submitRating} style={{ border: 0, borderRadius: 5, padding: '10px 16px', background: secondary[theme], color: '#fff', cursor: 'pointer' }}>Enviar avaliação</button></div>
      </div>
    </ModalGeneric>}
  </>;
};
